import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Box, Card, Icon, Link, Text, Toggle } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import {
  OrdersAutoCancelExpirationTime,
  OrdersAutoCancelPaymentStatusConfiguration,
} from '@tiendanube/common/src/domains/orders/orders';
import { OrdersAutoCancelPaymentMethod } from '@tiendanube/common/src/enums';
import { AlertSessionStorage } from 'App/components';
import { HELP_LINKS_ORDER_RECOVERY } from 'commons/constants';
import { useTranslationLanguage } from 'commons/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import PaymentMethodConfig from './PaymentMethodConfig';
import { CHECKOUT_AVG_EXPIRATION_MINUTES } from '../../constants';
import './PaymentStatusConfig.scss';

interface PaymentMethodsConfigProps {
  configuration: OrdersAutoCancelPaymentStatusConfiguration;
  disabled: boolean;
  onChange: (configuration: OrdersAutoCancelPaymentStatusConfiguration) => void;
  paymentMethodsWithError: OrdersAutoCancelPaymentMethod[];
}

function PaymentMethodsConfig({
  configuration,
  disabled,
  onChange,
  paymentMethodsWithError = [],
}: Readonly<PaymentMethodsConfigProps>): JSX.Element {
  const t = useTranslationOrders('autoCancel');
  const language = useTranslationLanguage();
  const url = HELP_LINKS_ORDER_RECOVERY[language];

  const [paymentMethodsConfig, setPaymentMethodsConfig] =
    useState<OrdersAutoCancelPaymentStatusConfiguration>(configuration);

  const [selectedSubceedsCheckout, setSelectedSubceedsCheckout] =
    useState(false);

  // Track which payment methods should be focused after enabling toggle
  const [focusedPaymentMethods, setFocusedPaymentMethods] = useState<
    Set<OrdersAutoCancelPaymentMethod>
  >(new Set());

  useEffect(() => {
    setPaymentMethodsConfig(configuration);
    setSelectedSubceedsCheckout(
      Object.entries(configuration).some(
        ([_, config]) =>
          config.isActive &&
          config.expirationTime &&
          config.expirationTime <= CHECKOUT_AVG_EXPIRATION_MINUTES,
      ),
    );
  }, [configuration]);

  const handleToggleChange = (
    e: ChangeEvent,
    paymentMethod: OrdersAutoCancelPaymentMethod,
    isActive: boolean,
  ) => {
    if (disabled) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    // Check if we're enabling a toggle that was previously disabled
    const wasDisabled = !paymentMethodsConfig[paymentMethod]?.isActive;
    const isBeingEnabled = isActive && wasDisabled;

    const newConfig = {
      ...paymentMethodsConfig,
      [paymentMethod]: {
        ...paymentMethodsConfig[paymentMethod],
        isActive,
      },
    };
    setPaymentMethodsConfig(newConfig);
    onChange(newConfig);

    // If toggle is being enabled (from disabled to enabled), mark for focus
    if (isBeingEnabled) {
      setFocusedPaymentMethods((prev) => new Set(prev).add(paymentMethod));
      // Clear focus state after the focus has been applied (using the same timeout pattern as useAutoFocus)
      setTimeout(() => {
        setFocusedPaymentMethods((prev) => {
          const newSet = new Set(prev);
          newSet.delete(paymentMethod);
          return newSet;
        });
      });
    }
  };

  const handleExpirationTimeChange = (
    paymentMethod: OrdersAutoCancelPaymentMethod,
    value: number,
  ) => {
    const newConfig = {
      ...paymentMethodsConfig,
      [paymentMethod]: {
        ...paymentMethodsConfig[paymentMethod],
        expirationTime: value as OrdersAutoCancelExpirationTime,
      },
    };
    setPaymentMethodsConfig(newConfig);
    setSelectedSubceedsCheckout(
      Object.entries(newConfig).some(
        ([_, config]) =>
          config.isActive &&
          config.expirationTime &&
          config.expirationTime <= CHECKOUT_AVG_EXPIRATION_MINUTES,
      ),
    );
    onChange(newConfig);
  };

  const paymentMethodsWithErrorSet = useMemo(
    () => new Set(paymentMethodsWithError),
    [paymentMethodsWithError],
  );

  return (
    <Card>
      <Card.Header title={t('paymentMethodsTitle')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Text>{t('paymentMethodsDesc')}</Text>
          {Object.entries(paymentMethodsConfig).map(
            ([paymentMethod, config]) => (
              <Box
                gap="2"
                key={`box-${paymentMethod}`}
                display="flex"
                flexDirection="column"
              >
                <div className={disabled ? 'disabled-toggle' : ''}>
                  <Toggle
                    name={`paymentMethodToggle-${paymentMethod}`}
                    label={t(`paymentMethod.${paymentMethod}`)}
                    checked={config?.isActive}
                    onChange={(e) => {
                      handleToggleChange(
                        e,
                        paymentMethod as OrdersAutoCancelPaymentMethod,
                        !config?.isActive,
                      );
                    }}
                  />
                </div>
                {config?.isActive && (
                  <PaymentMethodConfig
                    paymentMethod={
                      paymentMethod as OrdersAutoCancelPaymentMethod
                    }
                    expirationTime={config?.expirationTime || null}
                    onExpirationTimeChange={handleExpirationTimeChange}
                    hasError={paymentMethodsWithErrorSet.has(
                      paymentMethod as OrdersAutoCancelPaymentMethod,
                    )}
                    disabled={disabled}
                    shouldFocus={focusedPaymentMethods.has(
                      paymentMethod as OrdersAutoCancelPaymentMethod,
                    )}
                  />
                )}
              </Box>
            ),
          )}
          {selectedSubceedsCheckout && (
            <AlertSessionStorage
              appearance="neutral"
              keyName="autoCancelCheckoutAlert"
            >
              <Text>{t('checkoutAlert.description')}</Text>
              <Link as="a" href={url} target="_blank">
                <Box display="flex" gap="2">
                  {t('checkoutAlert.link')}
                  <Icon source={<ExternalLinkIcon />} />
                </Box>
              </Link>
            </AlertSessionStorage>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}

export default PaymentMethodsConfig;
